// @ts-strict-ignore
import Highcharts from 'highcharts';
import React, { ReactElement } from 'react';
import jQuery from 'jquery';
import _ from 'lodash';
import { ImageContent } from '@/hybrid/annotation/ckEditorPlugins/components/ImageContent.molecule';
import { ReactJsonContent } from '@/hybrid/annotation/ckEditorPlugins/components/ReactJsonContent.molecule';
import { ContentCallbacks, CustomPlugin } from '@/hybrid/annotation/ckEditorPlugins/CkEditorPlugins.module';
import { Icon } from '@/hybrid/core/Icon.atom';
import classNames from 'classnames';
import {
  Visualization,
  VisualizationData,
} from '@/hybrid/annotation/ckEditorPlugins/components/content.utilities.constants';
import { CONTENT_MODEL_ATTRIBUTES } from '@/hybrid/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import { ChartViewActions, ChartViewData } from '@/hybrid/trend/ChartView.organism';
import { XyPlotContentProps } from '@/hybrid/scatterPlot/XyPlotContent.organism';
import { TableBuilderPropsOnlyBindings } from '@/hybrid/tableBuilder/TableBuilderPropsOnly.organism';
import { ChartViewContentProperties } from '@/hybrid/reportEditor/ChartViewContent.molecule';
import { RangeExport } from '@/trendData/duration.store';
import { deserializeRange } from '@/hybrid/datetime/dateTime.utilities';
import { formatCapsuleLabel } from '@/hybrid/utilities/chartHelper.utilities';
import { ITEM_TYPES } from '@/trendData/trendData.constants';

const MINIMAP_HEIGHT = 35;

export enum SubVisualization {
  SCATTER_PLOT = 'scatterPlot',
  DENSITY_PLOT = 'densityPlot',
}

/**
 * Used for interactive content in Calendar, Chain, or Capsule Mode to transform props that had been serialized into
 * the correct form for ChartViewContent
 * @param props
 */
export const transformTrendProps = (props: ChartViewContentProperties) => {
  // Capsule items had their formatter function removed when they were serialized, so add them back
  _.forEach(props.data.items, (item) => {
    if (item.capsuleSetId && item.dataLabels) {
      item.dataLabels.formatter = formatCapsuleLabel;
    }
  });

  // chartDrawService wants capsuleDisplayItems to have their capsules attached.
  const mapOfConditionIdToCapsulesList = _.groupBy(props.data.capsules, 'isChildOf');
  _.forEach(props.data.items, (item) => {
    if (item.itemType === ITEM_TYPES.CAPSULE) {
      item.capsules = mapOfConditionIdToCapsulesList[item.capsuleSetId] ?? [];
    }
  });

  props.configuration.isInTopic = true;
  (props.data.sqDurationStoreData.displayRange as RangeExport) = deserializeRange(props.data.serializedDateRange);
  return props;
};

/**
 * Used for interactive content in Calendar, Chain, or Capsule Mode to cleanse props prior to saving them to the
 * backend. Here, we can de-duplicate data to reduce our footprint as much as possible.
 * @param props
 */
export function cleanseTrendProps(props: { data: ChartViewData }): typeof props {
  const newProps = _.cloneDeep(props);
  _.forEach(newProps.data.items, (item) => {
    // Remove data.items.samples because it duplicates data.items.data. and isn't used
    item.samples = undefined;
    // Remove data.items.capsules b/c it duplicates data.items.data and data.capsules. Will add back when deserialized.
    item.capsules = undefined;
  });
  return newProps;
}

type VisualizationContentProps = XyPlotContentProps | TableBuilderPropsOnlyBindings | ChartViewContentProperties;
export const VisualizationContentPropsTransformer: {
  [key in Visualization]: (props: VisualizationContentProps) => VisualizationContentProps;
} = {
  [Visualization.XY_PLOT]: _.identity,
  [Visualization.TABLE]: _.identity,
  [Visualization.TREND]: transformTrendProps,
  [Visualization.NONE]: _.identity,
};

export const VisualizationContentProps: { [key in Visualization]: any } = {
  [Visualization.XY_PLOT]: {
    canShowPlot: () => true,
    fetchScatterPlot: _.noop,
    clearPointerValues: _.noop,
    setSelectedRegion: _.noop,
    zoomToRegion: _.noop,
    createAxisControl: _.noop,
    setSelectors: _.noop,
    rangeEditingEnabled: false,
    autoUpdateDisabled: true,
  },
  [Visualization.TREND]: {
    actions: {
      clearPointerValues: _.noop,
      setPointerValues: _.noop,
      selectItems: _.noop,
      updateDisplayRangeTimes: _.noop,
      setYExtremes: _.noop,
      setSelectedRegion: _.noop,
      setCapsuleTimeOffsets: _.noop,
      findCapsuleItem: _.noop,
    } as ChartViewActions,
  },
  [Visualization.TABLE]: {
    displayMetricOnTrend: () => {},
  },
  [Visualization.NONE]: {},
};

export interface ContentVisualizationProps {
  src: string;
  contentId: string;
  noMargin?: boolean;
  border?: boolean;
  style: any;
  target: any;
  loading: boolean;
  onLoad: () => void;
  onError: (error: string, errorCode: number) => void;
  onMeasurementsReady: (height, width) => void;
  showMenu?: () => void;
  errorClass?: string;
  error?: string;
  propertyOverrides?: any;
  stopUpdate?: boolean;
  height?: number;
  width?: number;
}

export function renderContentVisualization(props: ContentVisualizationProps, react: boolean) {
  return react ? <ReactJsonContent {...props} /> : <ImageContent {...props} />;
}

/**
 * Sends the data for a component to the renderer. The renderer then serializes the data to JSON and stores it on
 * the backend for use as interactive content. Because it is serialized to JSON all functions are removed and an
 * error is thrown if there are any values that are not primitives (i.e. a moment object).
 * @param data - The data to store. Usually all the props of the component.
 * @param transformers - A map of keys and associated functions to transform the data. Useful for cleansing.
 */
export function provideVisualizationData(
  data: VisualizationData,
  transformers: { [s: string]: (value: any) => any } = {},
) {
  const cleansedData = _.chain(data)
    .omitBy(_.isFunction)
    .mapValues((value, key) => (transformers[key] ? transformers[key](value) : value))
    .value();
  window.seeqGetVisualizationData = () => Promise.resolve(cleansedData);
}

export function serializeDisplayRange({ start, end, duration }): {
  start: number;
  end: number;
  duration: number;
} {
  return {
    start: start.valueOf(),
    end: end.valueOf(),
    duration: duration.valueOf(),
  };
}

export function setContentPropertyOverrides(editor: Editor, contentId: string, newProps: any) {
  const contentCallbacks: ContentCallbacks = editor.config.get(CustomPlugin.Content).contentCallbacks;
  const model = contentCallbacks.getCurrentModel(contentId);

  editor.model.change((writer) =>
    writer.setAttribute(
      CONTENT_MODEL_ATTRIBUTES.PROPERTY_OVERRIDES,
      _.merge({}, model.getAttribute(CONTENT_MODEL_ATTRIBUTES.PROPERTY_OVERRIDES) ?? {}, newProps),
      model,
    ),
  );
}

export function clearContentPropertyOverrides(editor: Editor, contentId: string) {
  const contentCallbacks: ContentCallbacks = editor.config.get(CustomPlugin.Content).contentCallbacks;
  const model = contentCallbacks.getCurrentModel(contentId);
  if (model) {
    editor.model.change((writer) => writer.setAttribute(CONTENT_MODEL_ATTRIBUTES.PROPERTY_OVERRIDES, undefined, model));
  }
}

/**
 * A helper to auto resize the chart associated with a given content ID. This does nothing but call afterRefresh if
 * no chart is found (e.g. table content)
 */
export function refreshHighchartsSize(
  contentId: string,
  options: {
    height: number;
    width: number;
    visualization?: Visualization;
    subVisualization?: SubVisualization;
  },
  afterRefresh: () => void = _.noop,
) {
  const highchartsId = _.toNumber(
    jQuery(`[data-seeq-content="${contentId}"] [data-highcharts-chart]`).attr('data-highcharts-chart'),
  );
  if (_.isNumber(highchartsId) && Highcharts.charts[highchartsId]) {
    Highcharts.charts[highchartsId].setSize(options.width, options.height);

    const minimapChartId = highchartsId + 1;
    if (
      options.visualization === Visualization.XY_PLOT &&
      options.subVisualization === SubVisualization.SCATTER_PLOT &&
      Highcharts.charts[minimapChartId]
    ) {
      Highcharts.charts[minimapChartId].setSize(null, MINIMAP_HEIGHT);
    }
    afterRefresh();
  }
}

/**
 * A helper function that creates a border trigger on the top right corner of a seeq component.
 * If more than one single borderTrigger is added on the same component, we should use the
 * offsetX classes for offsetting the position based on how many previous components are there.
 *
 * Classes this component supports:
 * offset1, offset2, offset3 -> offsets the position based on previous number of border triggers
 * alwaysVisible -> in some cases in viewMode we'd want to have this trigger always visible. This
 *                  class will forder the trigger to always be visible and move it inside the
 *                  iframe so it doesn't show over any possible outside text
 *
 * @param callback Click callback
 * @param classes Specific classes to add
 * @param icon Icon to use
 * @param testId TestID to add to component
 * @param ref If needed a React ref
 * @returns DIV with the specific icon that is used for the border trigger
 */
export function renderBorderCommand(
  callback: () => void,
  classes: any,
  icon: string,
  testId: string,
  ref: React.MutableRefObject<any> = null,
): ReactElement {
  return (
    <div className={classNames('borderTrigger', classes)} data-testid={testId} ref={ref} onClick={callback}>
      <Icon icon={icon} />
    </div>
  );
}
